export type PickingStrategy = 'Fifo' | 'Lifo' | 'Fefo' | 'Batch' | 'GrnDate';
export type MaterialValidationRule = 'MaterialOnly' | 'MaterialBatch' | 'MaterialBarcode';
export type GrnLabelPrintTiming = 'BeforeQc' | 'AfterQc';
export type StockUpdateTrigger = 'OnGrn' | 'OnQc' | 'OnPutaway';
export type ProductionMaterialIssueMode = 'PickList' | 'BatchSelect' | 'ValidationOnly';
export type ProductionPickListGenerationMode = 'Auto' | 'Manual';
export type DispatchPickListStrategy = 'BatchWiseFifo' | 'ProductionDateFifo' | 'Fefo';
export type DispatchPickListGenerationMode = 'Auto' | 'Manual';
export type DispatchMaterialScope = 'FG' | 'All';
export type DispatchTypeScope = 'Customer' | 'Warehouse' | 'Both';
export type DateFormatOption = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD-MMM-YYYY' | 'MMM D, YYYY';

export const DATE_FORMAT_OPTIONS: { value: DateFormatOption; sample: string }[] = [
  { value: 'DD/MM/YYYY', sample: '31/12/2026' },
  { value: 'MM/DD/YYYY', sample: '12/31/2026' },
  { value: 'YYYY-MM-DD', sample: '2026-12-31' },
  { value: 'DD-MMM-YYYY', sample: '31-Dec-2026' },
  { value: 'MMM D, YYYY', sample: 'Dec 31, 2026' },
];

export interface ProcessConfigurationDto {
  id: string;
  companyId: string;
  purchaseRequestEnabled: boolean;
  purchaseOrderEnabled: boolean;
  gateEntryEnabled: boolean;
  qcEnabled: boolean;
  qcCheckpointsEnabled: boolean;
  qcCheckpointsMandatory: boolean;
  putawayEnabled: boolean;
  enableGrnLabelPrinting: boolean;
  grnLabelPrintTiming: GrnLabelPrintTiming;
  stockUpdateTrigger: StockUpdateTrigger;
  // Master switch for the Material Request screen itself — mirrors enableGrnLabelPrinting's role
  // for GRN Label Print (hides the screen from the left nav when off). The two flags below then
  // choose which creation mode(s) it offers once on. materialRequestBomEnabled is unused — Material
  // Request no longer has a BOM-based mode; kept only so the field isn't a disruptive removal.
  materialRequestEnabled: boolean;
  materialRequestManualEnabled: boolean;
  materialRequestExcelImportEnabled: boolean;
  materialRequestBomEnabled: boolean;
  pickingStrategyDefault: PickingStrategy;
  materialValidationRule: MaterialValidationRule;
  allowPartialPicking: boolean;
  allowBoxPicking: boolean;
  allowLoosePicking: boolean;
  packingEnabled: boolean;
  packingLabelEnabled: boolean;
  // Master switch for the Gate Exit screen — independent of packingEnabled/packingLabelEnabled,
  // since it applies whether or not Packing ran.
  gateExitEnabled: boolean;
  // Master switch for 3PL client-scoped inventory — off by default. When on, the Client master
  // becomes visible and Client selection becomes mandatory on stock-affecting create forms.
  thirdPartyLogisticsEnabled: boolean;
  // Sub-switches of thirdPartyLogisticsEnabled — only meaningful when that's also on. Independently
  // hide the Billing screens/nav and gate whether Client Portal logins can authenticate at all.
  billingEnabled: boolean;
  clientPortalEnabled: boolean;
  // Adjustments — each an independent master switch for its own screen, same nav-hiding role as
  // materialRequestEnabled/dispatchEnabled above.
  stockAdjustmentEnabled: boolean;
  stockTransferEnabled: boolean;
  cycleCountEnabled: boolean;
  returnNoteEnabled: boolean;
  shelfLifeManagementEnabled: boolean;
  shelfLifeWarningDays: number;
  blockExpiredStock: boolean;
  barcodeMandatory: boolean;
  documentAutoNumberEnabled: boolean;
  approvalWorkflowEnabled: boolean;
  // The one common master switch for the ENTIRE Production module — Production Plan, Material
  // Request, Production Receipt, Production Pick List, and the BOM master tab all require this to
  // be on, in addition to whichever of their own individual flags they each also need (see
  // productionEnabled/materialRequestEnabled/productionReceiptEnabled, each still independently
  // selectable once this master is on). Off hides every one of them at once.
  productionModuleEnabled: boolean;
  productionEnabled: boolean;
  // Which creation mode(s) the Production Plan screen offers — at least one should stay enabled;
  // ProductionOrdersPage falls back to BOM Request if a company somehow disables all three.
  productionPlanManualEnabled: boolean;
  productionPlanExcelImportEnabled: boolean;
  productionPlanBomEnabled: boolean;
  // Independent of productionEnabled — Production Receipt (and Production Label Print/Production
  // Putaway, both downstream of it) can be used on its own, e.g. by a company that only raises
  // Material Requests and never uses formal Production Orders/BOMs.
  productionReceiptEnabled: boolean;
  productionMaterialIssueMode: ProductionMaterialIssueMode;
  productionPickListGenerationMode: ProductionPickListGenerationMode;
  // Master switch for the ENTIRE Job Work (subcontracting) module — off by default, since it's a
  // net-new opt-in capability (same rationale as thirdPartyLogisticsEnabled). Hides every Job Work
  // screen and the Job Work Vendor tick on the Supplier master when off.
  jobWorkEnabled: boolean;
  // Which creation mode(s) the Job Work Order screen offers for choosing the inputs to send out —
  // same pair pattern as productionPlanBomEnabled/productionPlanManualEnabled. Only meaningful
  // while jobWorkEnabled is on.
  jobWorkBomEnabled: boolean;
  jobWorkManualEnabled: boolean;
  // Master switch for the ENTIRE Weight-Based Production module — a sibling module to Job Work
  // (own Plan/Kiosk/Label Print/Hardware Configuration screens), off by default, same net-new
  // opt-in rationale as jobWorkEnabled/thirdPartyLogisticsEnabled.
  weightProductionEnabled: boolean;
  // "Block" | "AllowWithWarning" | "RequireApproval" | "Allow" — see
  // ProcessConfiguration.WeightProductionOverProductionPolicy on the server.
  weightProductionOverProductionPolicy: 'Block' | 'AllowWithWarning' | 'RequireApproval' | 'Allow';
  // Which single tab the Weight-Based Production Plan screen offers — true shows only BOM Based
  // (raw materials are issued from stock before anything can be weighed), false shows only Direct
  // (items are simply weighed and labelled). See ProcessConfiguration.WeightProductionRawMaterialHandlingEnabled.
  weightProductionRawMaterialHandlingEnabled: boolean;
  // Master switch for the 3 new outbound Dispatch screens (Dispatch Order/Pick List/Material
  // Issue) — independent of materialRequestEnabled/productionEnabled.
  dispatchEnabled: boolean;
  dispatchPickListStrategy: DispatchPickListStrategy;
  dispatchPickListGenerationMode: DispatchPickListGenerationMode;
  // Which materials the Dispatch Order line-item picker offers — "FG" (default) restricts it to
  // Finished Goods, "All" lifts that restriction. See DispatchOrdersPage.tsx.
  dispatchMaterialScope: DispatchMaterialScope;
  // Which DispatchOrder.destinationType(s) the Dispatch Order screen offers — "Customer" (default),
  // "Warehouse", or "Both". Independent of weightProductionEnabled — see
  // ProcessConfiguration.DispatchTypeScope on the server for why that used to be conflated.
  dispatchTypeScope: DispatchTypeScope;
  dateFormat: DateFormatOption;
  // Master switch for the TV Display feature (header launcher icon + the /tv-dashboard route).
  tvDisplayEnabled: boolean;
  // Platform-level cap on how many Branches this company may create, settable only by SuperAdmin
  // via the Companies console — the self-service ProcessConfigurationPage renders it read-only.
  // Null means unlimited. The equivalent Warehouse limit is per-Branch — see BranchDto.maxWarehouses.
  maxBranches: number | null;
}

export type UpdateProcessConfigurationRequest = Omit<ProcessConfigurationDto, 'id' | 'companyId'>;
